import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Trash2, Loader2, Car, FileText, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog de suppression en masse avec cascade :
 * - Récupère le preview agrégé (véhicules orphelins, vehicle_contracts, résiliations)
 * - Permet de choisir ce qui doit être supprimé en cascade
 */
export default function BulkDeleteContractsDialog({
  open,
  onOpenChange,
  contractIds,
  token,
  onSuccess,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [opts, setOpts] = useState({
    delete_vehicles: false,
    delete_vehicle_contracts: true,
    delete_resiliations: true,
  });

  useEffect(() => {
    if (!open || !contractIds?.length) return;
    setPreview(null);
    setLoading(true);
    axios
      .post(
        `${API}/api/engagements/contrats/bulk-delete-preview`,
        { contract_ids: contractIds },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setPreview(res.data);
        setOpts({
          delete_vehicles: (res.data.vehicles_would_be_orphan || 0) > 0,
          delete_vehicle_contracts: (res.data.vehicle_contracts_count || 0) > 0,
          delete_resiliations: (res.data.resiliations_count || 0) > 0,
        });
      })
      .catch(() => toast.error("Erreur lors du preview"))
      .finally(() => setLoading(false));
  }, [open, contractIds, token]);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(
        `${API}/api/engagements/contrats/bulk-delete`,
        { contract_ids: contractIds, ...opts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const c = res.data?.cascade || {};
      const bits = [`${c.contracts_deleted} contrat(s)`];
      if (c.vehicles_deleted) bits.push(`${c.vehicles_deleted} véhicule(s)`);
      if (c.vehicles_dissociated) bits.push(`${c.vehicles_dissociated} dissocié(s)`);
      if (c.vehicle_contracts_deleted) bits.push(`${c.vehicle_contracts_deleted} contrat véhicule archivé(s)`);
      if (c.resiliations_deleted) bits.push(`${c.resiliations_deleted} résiliation(s)`);
      toast.success(`Suppression : ${bits.join(" · ")}`);
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Supprimer {contractIds?.length || 0} contrat(s) — que faut-il conserver ?
          </DialogTitle>
          <DialogDescription>
            Choisissez ce qui doit être supprimé en cascade. Cette action est irréversible.
          </DialogDescription>
        </DialogHeader>

        {loading || !preview ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-3 my-2">
            {/* Véhicules */}
            {preview.vehicles.length > 0 ? (
              <div className="p-3 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={opts.delete_vehicles}
                    onChange={(e) => setOpts((o) => ({ ...o, delete_vehicles: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-red-600"
                    disabled={preview.vehicles_would_be_orphan === 0}
                    data-testid="bulk-delete-vehicles"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      <Car className="w-4 h-4 text-indigo-600" />
                      Supprimer les véhicules Parc Auto orphelins
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        {preview.vehicles_would_be_orphan} orphelin(s)
                      </span>
                    </div>
                    {preview.vehicles_would_be_dissociated > 0 && (
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        {preview.vehicles_would_be_dissociated} véhicule(s) seront dissociés (autres engagements liés)
                      </div>
                    )}
                    <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                      {preview.vehicles.slice(0, 5).map((v) => (
                        <div key={v.id} className="font-mono truncate">
                          • {v.brand} {v.model} · {v.registration}
                          {v.would_be_orphan ? (
                            <span className="text-amber-700"> — orphelin</span>
                          ) : (
                            <span className="text-blue-700"> — dissocié</span>
                          )}
                        </div>
                      ))}
                      {preview.vehicles.length > 5 && (
                        <div className="text-slate-400">…et {preview.vehicles.length - 5} autres</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-slate-50 text-xs text-slate-500 flex items-center gap-2">
                <Car className="w-4 h-4" />
                Aucun véhicule Parc Auto impacté.
              </div>
            )}

            {/* Vehicle contracts */}
            {preview.vehicle_contracts_count > 0 ? (
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={opts.delete_vehicle_contracts}
                  onChange={(e) => setOpts((o) => ({ ...o, delete_vehicle_contracts: e.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-red-600"
                  data-testid="bulk-delete-vcs"
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-600" />
                    Archiver les contrats véhicule ({preview.vehicle_contracts_count})
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    LLD, LOA, assurance auto — marqués comme supprimés dans le Parc Auto.
                  </div>
                </div>
              </label>
            ) : (
              <div className="p-3 rounded-lg bg-slate-50 text-xs text-slate-500 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Aucun contrat véhicule à archiver.
              </div>
            )}

            {/* Résiliations */}
            {preview.resiliations_count > 0 ? (
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={opts.delete_resiliations}
                  onChange={(e) => setOpts((o) => ({ ...o, delete_resiliations: e.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-red-600"
                  data-testid="bulk-delete-resils"
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-600" />
                    Supprimer les lettres de résiliation ({preview.resiliations_count})
                  </div>
                </div>
              </label>
            ) : (
              <div className="p-3 rounded-lg bg-slate-50 text-xs text-slate-500 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Aucune lettre de résiliation liée.
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleting || loading}
            data-testid="bulk-delete-confirm"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Supprimer {contractIds?.length} contrat(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
